import type ConversationType from 'owa-service/lib/contract/ConversationType';
import findIndexByInstancekey from './helpers/findIndexByInstancekey';
import onAddOrUpdateRow from './helpers/onAddOrUpdateRow';
import reloadTable from '../tables/reloadTable';
import removeRowsFromListViewStoreInternal from '../triage/removeRowsFromListViewStoreInternal';
import type TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import { action } from 'satcheljs/lib/legacy';
import { clearAndRefreshRowDataUsedByReadingPane } from './helpers/clearAndRefreshRowDataUsedByReadingPane';
import type { MailListRowDataType } from 'mini-mail-list-store/lib/store/schema/MailListRowDataType';
import { removeFromTombstone } from 'mini-mail-list-tombstone';

/**
 * This class is responsible for handling the notifications.
 * onAddOrUpdateNotification for RowAdd and RowModify notification types
 * onDeleteNotification for RowDelete notification types
 * onReloadNotification for Reload notification type
 */

/**
 * This function handles rowAdd notifications.
 * @param row present in the notification payload
 * @param tableview for which notification has arrived
 * which we will insert the new row payload
 */
export const onAddRow = action('rowNotificationHandler.onAddRow')(function onAddRow(
    row: MailListRowDataType,
    tableView: TableView
) {
    // Increment table size
    tableView.totalRowsInView++;

    // Handle adding the row to store
    onAddOrUpdateRow(row, tableView);
});

/**
 * This function handles rowModified notifications.
 * @param row present in the notification payload
 * @param tableview for which notification has arrived
 * which we will insert the new row payload
 */
export const onUpdateRow = action('rowNotificationHandler.onUpdateRow')(function onUpdateRow(
    row: MailListRowDataType,
    tableView: TableView
) {
    // Handle adding the row to store
    onAddOrUpdateRow(row, tableView);
});

/**
 * This function handles RowDeleted notification.
 * @param conversation present in the notification payload // TODO: remove conversation. make row.
 * @param tableview for which notification has arrived
 */
export const onDeleteRow = action('rowNotificationHandler.onDeleteRow')(function onDeleteRow(
    conversation: ConversationType,
    tableView: TableView
) {
    if (!conversation.InstanceKey) {
        // row being deleted should not have a null instance key
        throw new Error('onDeleteNotification: Conversation instanceKey should not be null');
    }

    // Decrement table size
    tableView.totalRowsInView--;

    // Remove the row
    const indexOfElement = findIndexByInstancekey(conversation, tableView);
    if (indexOfElement >= 0) {
        const conversationId = tableView.itemIds[indexOfElement];
        removeRowsFromListViewStoreInternal([conversationId], tableView);
    }

    // Always remove from tombstone as delete notification
    // supersedes all other reasons, so that we clean up the tombstone properly on delete
    removeFromTombstone([conversation.InstanceKey], tableView.serverFolderId);
});

/**
 * This function handles the reload notifications
 * @param tableView for which the notification has arrived
 * TBD - Revisit the reload table logic - https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/11800
 */
export const onReload = action('rowNotificationHandler.onReload')(function onReload(
    tableView: TableView
) {
    clearAndRefreshRowDataUsedByReadingPane(tableView);

    // Reload the table
    reloadTable(tableView);
});
